import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PriceAlerts = () => {
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      commodity: 'White Sugar M30',
      market: 'Pune APMC',
      condition: 'above',
      targetPrice: 3900,
      currentPrice: 3850,
      active: true,
      triggered: false,
      frequency: 'realtime',
      createdAt: '2024-12-25'
    },
    {
      id: 2,
      commodity: 'Raw Sugar S30',
      market: 'Mumbai Market',
      condition: 'below',
      targetPrice: 3600,
      currentPrice: 3620,
      active: true,
      triggered: false,
      frequency: 'daily',
      createdAt: '2024-12-24'
    },
    {
      id: 3,
      commodity: 'Premium Sugar',
      market: 'Kolhapur Zone',
      condition: 'above',
      targetPrice: 4000,
      currentPrice: 4100,
      active: true,
      triggered: true,
      frequency: 'realtime',
      createdAt: '2024-12-20'
    }
  ]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAlert, setNewAlert] = useState({
    commodity: 'White Sugar M30',
    market: 'Pune APMC',
    condition: 'above',
    targetPrice: '',
    frequency: 'realtime'
  });

  const commodityOptions = [
    'White Sugar M30',
    'Raw Sugar S30',
    'Premium Sugar',
    'Refined Sugar'
  ];

  const marketOptions = [
    'Pune APMC',
    'Mumbai Market',
    'Kolhapur Zone',
    'Delhi NCR',
    'Bangalore Market'
  ];

  const handleToggleAlert = (id) => {
    setAlerts(alerts?.map((alert) =>
      alert?.id === id ? { ...alert, active: !alert?.active } : alert
    ));
  };

  const handleDeleteAlert = (id) => {
    setAlerts(alerts?.filter((alert) => alert?.id !== id));
  };

  const handleCreateAlert = () => {
    if (!newAlert?.targetPrice) return;

    const alert = {
      id: alerts?.length + 1,
      ...newAlert,
      currentPrice: 3850,
      active: true,
      triggered: false,
      createdAt: new Date()?.toISOString()?.split('T')?.[0]
    };

    setAlerts([...alerts, alert]);
    setShowCreateModal(false);
    setNewAlert({
      commodity: 'White Sugar M30',
      market: 'Pune APMC',
      condition: 'above',
      targetPrice: '',
      frequency: 'realtime'
    });
  };

  const alertStats = {
    total: alerts?.length,
    active: alerts?.filter((a) => a?.active)?.length,
    triggered: alerts?.filter((a) => a?.triggered)?.length
  };

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <div className="bg-card border border-border rounded-lg p-2 md:p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-1 md:mb-2">
            <span className="text-xs md:text-sm text-muted-foreground">Total Alerts</span>
            <Icon name="Bell" size={16} className="text-primary hidden md:block" />
          </div>
          <div className="text-lg md:text-2xl font-bold text-foreground">{alertStats?.total}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-2 md:p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-1 md:mb-2">
            <span className="text-xs md:text-sm text-muted-foreground">Active Alerts</span>
            <Icon name="BellRing" size={16} className="text-green-500 hidden md:block" />
          </div>
          <div className="text-lg md:text-2xl font-bold text-foreground">{alertStats?.active}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-2 md:p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-1 md:mb-2">
            <span className="text-xs md:text-sm text-muted-foreground">Triggered</span>
            <Icon name="AlertCircle" size={16} className="text-orange-500 hidden md:block" />
          </div>
          <div className="text-lg md:text-2xl font-bold text-foreground">{alertStats?.triggered}</div>
        </div>
      </div>

      {/* Create Alert Button */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground">Your Price Alerts</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Get notified when prices reach your target levels
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary text-white rounded-lg text-xs md:text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
        >
          <Icon name="Plus" size={16} className="md:hidden" />
          <Icon name="Plus" size={18} className="hidden md:block" />
          <span>Create Alert</span>
        </button>
      </div>

      {/* Alerts List */}
      <div className="space-y-2 md:space-y-4">
        {alerts?.map((alert) => (
          <div
            key={alert?.id}
            className={`bg-card border rounded-lg p-3 md:p-5 transition-all ${
              alert?.triggered
                ? 'border-orange-500 bg-orange-50/30'
                : alert?.active
                ? 'border-border' :'border-border opacity-60'
            }`}
          >
            <div className="flex items-start justify-between gap-2 md:gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 md:gap-3 mb-2 md:mb-3">
                  <div
                    className={`flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${
                      alert?.triggered
                        ? 'bg-orange-100 text-orange-600' :'bg-primary/10 text-primary'
                    }`}
                  >
                    <Icon name={alert?.triggered ? 'AlertCircle' : 'TrendingUp'} size={16} className="md:hidden" />
                    <Icon name={alert?.triggered ? 'AlertCircle' : 'TrendingUp'} size={20} className="hidden md:block" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm md:text-base text-foreground mb-0.5 md:mb-1 truncate">{alert?.commodity}</h4>
                    <p className="text-xs md:text-sm text-muted-foreground truncate">{alert?.market}</p>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-lg p-2 md:p-4 mb-2 md:mb-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5 md:mb-1">Alert Condition</div>
                      <div className="flex items-center gap-1 md:gap-2">
                        <Icon name={alert?.condition === 'above' ? 'ArrowUp' : 'ArrowDown'} size={14} className="text-foreground md:hidden" />
                        <Icon name={alert?.condition === 'above' ? 'ArrowUp' : 'ArrowDown'} size={16} className="text-foreground hidden md:block" />
                        <span className="font-medium text-xs md:text-sm text-foreground">
                          {alert?.condition === 'above' ? 'Above' : 'Below'} ₹{alert?.targetPrice}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5 md:mb-1">Current Price</div>
                      <div className="font-bold text-sm md:text-base text-foreground">₹{alert?.currentPrice}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5 md:mb-1">Frequency</div>
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={12} className="text-muted-foreground md:hidden" />
                        <Icon name="Clock" size={14} className="text-muted-foreground hidden md:block" />
                        <span className="text-xs md:text-sm font-medium text-foreground capitalize">
                          {alert?.frequency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {alert?.triggered && (
                  <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-orange-600 bg-orange-50 rounded-lg p-2 md:p-3 mb-2 md:mb-3">
                    <Icon name="Zap" size={14} className="flex-shrink-0 md:hidden" />
                    <Icon name="Zap" size={16} className="flex-shrink-0 hidden md:block" />
                    <span className="font-medium">
                      Alert triggered! Price reached target.
                    </span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Calendar" size={10} className="md:hidden" />
                    <Icon name="Calendar" size={12} className="hidden md:block" />
                    <span className="hidden md:inline">Created: </span>
                    <span>{new Date(alert?.createdAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className={`flex items-center gap-1 ${alert?.active ? 'text-green-600' : 'text-gray-600'}`}>
                    <span className={`inline-block w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${alert?.active ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                    <span className="font-medium">{alert?.active ? 'Active' : 'Paused'}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 md:gap-2 flex-shrink-0">
                <button
                  onClick={() => handleToggleAlert(alert?.id)}
                  className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                    alert?.active
                      ? 'bg-green-100 text-green-600 hover:bg-green-200' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title={alert?.active ? 'Pause Alert' : 'Activate Alert'}
                >
                  <Icon name={alert?.active ? 'Pause' : 'Play'} size={16} className="md:hidden" />
                  <Icon name={alert?.active ? 'Pause' : 'Play'} size={18} className="hidden md:block" />
                </button>
                <button
                  onClick={() => handleDeleteAlert(alert?.id)}
                  className="p-1.5 md:p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                  title="Delete Alert"
                >
                  <Icon name="Trash2" size={16} className="md:hidden" />
                  <Icon name="Trash2" size={18} className="hidden md:block" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {alerts?.length === 0 && (
        <div className="text-center py-8 md:py-12 bg-card border border-border rounded-lg">
          <Icon name="Bell" size={36} className="mx-auto text-muted-foreground mb-3 md:hidden" />
          <Icon name="Bell" size={48} className="mx-auto text-muted-foreground mb-4 hidden md:block" />
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">No Alerts Created</h3>
          <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4 px-4">
            Create your first price alert to get notified when prices reach your target
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 md:px-6 py-2 md:py-3 bg-primary text-white rounded-lg text-xs md:text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Create Your First Alert
          </button>
        </div>
      )}

      {/* Create Alert Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 md:p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-lg md:text-xl font-bold text-foreground">Create Price Alert</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 md:p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={18} className="md:hidden" />
                <Icon name="X" size={20} className="hidden md:block" />
              </button>
            </div>

            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-xs md:text-sm font-medium text-foreground mb-1.5 md:mb-2">
                  Commodity
                </label>
                <select
                  value={newAlert?.commodity}
                  onChange={(e) => setNewAlert({ ...newAlert, commodity: e?.target?.value })}
                  className="w-full px-3 md:px-4 py-2 text-sm bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {commodityOptions?.map((commodity) => (
                    <option key={commodity} value={commodity}>
                      {commodity}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-foreground mb-1.5 md:mb-2">
                  Market
                </label>
                <select
                  value={newAlert?.market}
                  onChange={(e) => setNewAlert({ ...newAlert, market: e?.target?.value })}
                  className="w-full px-3 md:px-4 py-2 text-sm bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {marketOptions?.map((market) => (
                    <option key={market} value={market}>
                      {market}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-foreground mb-1.5 md:mb-2">
                  Alert Condition
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewAlert({ ...newAlert, condition: 'above' })}
                    className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
                      newAlert?.condition === 'above' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name="ArrowUp" size={14} className="md:hidden" />
                    <Icon name="ArrowUp" size={16} className="hidden md:block" />
                    <span>Above</span>
                  </button>
                  <button
                    onClick={() => setNewAlert({ ...newAlert, condition: 'below' })}
                    className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
                      newAlert?.condition === 'below' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name="ArrowDown" size={14} className="md:hidden" />
                    <Icon name="ArrowDown" size={16} className="hidden md:block" />
                    <span>Below</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-foreground mb-1.5 md:mb-2">
                  Target Price (₹ per quintal)
                </label>
                <input
                  type="number"
                  value={newAlert?.targetPrice}
                  onChange={(e) => setNewAlert({ ...newAlert, targetPrice: e?.target?.value })}
                  placeholder="Enter target price"
                  className="w-full px-3 md:px-4 py-2 text-sm bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-foreground mb-1.5 md:mb-2">
                  Notification Frequency
                </label>
                <select
                  value={newAlert?.frequency}
                  onChange={(e) => setNewAlert({ ...newAlert, frequency: e?.target?.value })}
                  className="w-full px-3 md:px-4 py-2 text-sm bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="realtime">Real-time</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                </select>
              </div>

              <div className="flex gap-2 md:gap-3 pt-2 md:pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-3 md:px-4 py-2 bg-muted text-foreground rounded-lg text-xs md:text-sm font-medium hover:bg-muted/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAlert}
                  disabled={!newAlert?.targetPrice}
                  className="flex-1 px-3 md:px-4 py-2 bg-primary text-white rounded-lg text-xs md:text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Alert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceAlerts;